import { useContext, useEffect } from 'react';

import { Flex, Spacer, token } from '@zillow/constellation';
import { Button, ModalTrigger, Paragraph, Text } from '@zillow/constellation-10';

import { ModularListingContext } from '../../../../../containers/CreateListingPage/context';

import { FreemiumContext } from '../../../../../components/Freemium/context';

import actions from '../../../../../containers/CreateListingPage/actions';

import numberUtils from '../../../../../utils/numberUtils';

import {
  useGetListingInquiries,
  useGetListingPrices,
  useGetPremiumComps,
  useGetPremiumOverview,
} from '../../../../../hooks/queries/freemium';

import { RoutingSelectors } from '../../../../../selectors';
import { useSelector } from '../../../../../selectors/useSelector';

import type { PremiumPricingGuidanceProps } from '../../../../../model/PremiumPricingGuidance';

import {
  StyledBar,
  StyledContainerGrid,
  StyledDataPoint,
  StyledDataPointContainer,
  StyledFlexBlur,
  StyledIconBackground,
  StyledIconContainer,
  StyledIconLock,
  StyledSpacer,
} from './StyledComponents';

const defaultProps = {
  isActiveProductTypePremium: false,
  pricingGuidance: null,
};

type PremiumPriceTipContentProps = {
  isActiveProductTypePremium: boolean;
  pricingGuidance: PremiumPricingGuidanceProps | null;
};

const PremiumPriceTipContent = ({ isActiveProductTypePremium, pricingGuidance }: PremiumPriceTipContentProps) => {
  const {
    freemiumState: {
      filters: { beds, filterType, propertyType },
    },
  } = useContext(FreemiumContext);

  const { reducerDispatch } = useContext(ModularListingContext);

  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));

  const { refetch: refetchGetListingPrices } = useGetListingPrices(
    { listingId, beds, filterType, propertyType },
    {
      enabled: isActiveProductTypePremium,
      keepPreviousData: true,
    },
  );

  const { refetch: refetchGetListingPerformance } = useGetListingInquiries(
    {
      listingId,
      filterType: filterType,
      beds: beds,
      propertyType: propertyType,
    },
    {
      enabled: isActiveProductTypePremium,
      keepPreviousData: true,
    },
  );

  const { refetch: refetchGetComps } = useGetPremiumComps(
    {
      listingId,
      beds: beds,
      filterType: filterType,
      propertyType: propertyType,
    },
    {
      enabled: isActiveProductTypePremium,
      keepPreviousData: true,
    },
  );

  const { refetch: refetchGetPremiumOverview } = useGetPremiumOverview(
    { listingId },
    { enabled: isActiveProductTypePremium },
  );

  const bodyContent = isActiveProductTypePremium
    ? 'See more data for your rental market to ensure your listing is competitively priced.'
    : 'Gain access to deeper insights and analytics to help you price your rental and optimize your listing.';

  useEffect(() => {
    if (listingId && isActiveProductTypePremium) {
      refetchGetPremiumOverview();
      refetchGetListingPrices();
      refetchGetComps();
      refetchGetListingPerformance();
    }
  }, [isActiveProductTypePremium, pricingGuidance?.property?.price]);

  const handleLearnMoreClick = () => {
    actions.updateIsPremiumPurchaseModalOpen({
      reducerDispatch,
      isPremiumPurchaseModalOpen: true,
    });
  };

  const priceToNumber = (price: string) => Number(price.replace(/[^0-9.-]+/g, ''));

  const calculatePricePercent = (price: string, marketMin: string, marketMax: string) => {
    const priceNum = priceToNumber(price);
    const marketMinNum = priceToNumber(marketMin);
    const marketMaxNum = priceToNumber(marketMax);
    return Math.round(((priceNum - marketMinNum) / (marketMaxNum - marketMinNum)) * 100);
  };

  const renderMarketPricingGraphic = () => {
    if (isActiveProductTypePremium && pricingGuidance && pricingGuidance?.marketMedian?.price) {
      const {
        property: { price: listingPrice },
        marketMedian: { price: medianPrice },
        marketMin: { price: marketMinPrice },
        marketMax: { price: marketMaxPrice },
        market25thPercentile: { price: market25thPercentilePrice },
        market75thPercentile: { price: market75thPercentilePrice },
        rentZestimateRangeLow: { price: rentZestimateLowPrice },
        rentZestimateRangeHigh: { price: rentZestimateHighPrice },
      } = pricingGuidance;

      const isPropertyPriceInRange = listingPrice
        ? priceToNumber(listingPrice) >= priceToNumber(marketMinPrice) &&
          priceToNumber(listingPrice) <= priceToNumber(marketMaxPrice)
        : false;

      return (
        <StyledSpacer marginY="md" position="relative">
          <Flex display="flex" justifyContent="center" paddingY="sm">
            <Text>
              <Text asChild textStyle="body-bold">
                <span>Your rent:</span>
              </Text>
              {listingPrice
                ? ` ${numberUtils.asCurrency(Number(numberUtils.removeDollarSign(listingPrice)))}`
                : ' Not set'}
            </Text>
          </Flex>
          <StyledContainerGrid>
            <StyledBar borderColor={token('colors.coolGray400')} gridColumnStart={1} gridColumnEnd={100} />
            {/* for 5500+ price mkts: do not show if these 2 values are equal to avoid weirdness in chart */}
            {market25thPercentilePrice !== market75thPercentilePrice && (
              <StyledBar
                borderColor={token('colors.blue400')}
                gridColumnStart={calculatePricePercent(market25thPercentilePrice, marketMinPrice, marketMaxPrice)}
                gridColumnEnd={calculatePricePercent(market75thPercentilePrice, marketMinPrice, marketMaxPrice)}
              />
            )}
          </StyledContainerGrid>
          {/* only plot listingPrice if it's within the market range */}
          {isPropertyPriceInRange && (
            <StyledDataPointContainer marginLeft={calculatePricePercent(listingPrice, marketMinPrice, marketMaxPrice)}>
              <StyledDataPoint />
            </StyledDataPointContainer>
          )}
          <Flex display="flex" justifyContent="space-between">
            <Text color="text.subtle">
              Min: {numberUtils.asCurrency(Number(numberUtils.removeDollarSign(marketMinPrice)))}
            </Text>
            <Text color="text.subtle">
              Max: {numberUtils.asCurrency(Number(numberUtils.removeDollarSign(marketMaxPrice)))}
            </Text>
          </Flex>
        </StyledSpacer>
      );
    }

    //  non premium static preview version
    return (
      <>
        <StyledSpacer marginY="md">
          <StyledContainerGrid isLocked={true}>
            <StyledBar borderColor={token('colors.coolGray400')} gridColumnStart={1} gridColumnEnd={100} />
            <StyledBar borderColor={token('colors.blue400')} gridColumnStart={10} gridColumnEnd={70} />
          </StyledContainerGrid>
          <StyledFlexBlur display="flex" justifyContent="space-between">
            <Text textStyle="body-sm">Min</Text>
            <Text textStyle="body-sm">Max</Text>
          </StyledFlexBlur>
          <StyledIconContainer>
            <StyledIconBackground>
              <Text textStyle="body-sm" css={{ display: 'inline-flex', alignItems: 'center' }} asChild>
                <span>
                  {' '}
                  <StyledIconLock />{' '}
                </span>
              </Text>
            </StyledIconBackground>
          </StyledIconContainer>
        </StyledSpacer>
      </>
    );
  };

  return (
    <div data-test-id="PremiumPriceTipContent">
      <Paragraph>{bodyContent}</Paragraph>
      {renderMarketPricingGraphic()}
      <Spacer>
        {isActiveProductTypePremium && (
          <>
            <ModalTrigger multiModalTriggerId="PremiumPricingGuidanceModal">
              <Button fluid={true}>Explore data</Button>
            </ModalTrigger>
          </>
        )}
        {!isActiveProductTypePremium && (
          <Button tone="brand" emphasis="outlined" fluid onClick={handleLearnMoreClick}>
            Learn more
          </Button>
        )}
      </Spacer>
    </div>
  );
};

PremiumPriceTipContent.defaultProps = defaultProps;

export default PremiumPriceTipContent;
